import { NavLink } from 'react-router-dom';
import { usePlatformPermissions } from '../../../hooks/usePlatformPermissions';
import { isAiSalesPreview, setAiSalesPreview } from '../lib/source';
import type { FunnelStage, KpiCard } from '../types/contracts';

export function EmptyState({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-14 text-center">
      <i className="ri-inbox-2-line text-3xl text-gray-300" />
      <h3 className="mt-3 text-sm font-bold text-gray-800">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-rose-100 bg-rose-50 px-6 py-10 text-center">
      <p className="text-sm font-semibold text-rose-700">{message || 'Something went wrong'}</p>
      <button type="button" onClick={onRetry} className="mt-3 px-3 py-1.5 text-xs font-bold rounded-lg bg-rose-600 text-white">
        Retry
      </button>
    </div>
  );
}

export function SkeletonBlock({ rows = 4 }: { rows?: number }) {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 rounded-xl bg-gray-100" />
      ))}
    </div>
  );
}

export function SourceBanner() {
  if (!isAiSalesPreview()) return null;
  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
      <span>Sample preview — not live production data.</span>
      <button
        type="button"
        className="font-bold underline"
        onClick={() => {
          setAiSalesPreview(false);
          window.location.reload();
        }}
      >
        Exit preview
      </button>
    </div>
  );
}

export function KpiGrid({ items }: { items: KpiCard[] }) {
  const labels: Record<string, string> = {
    conversations: 'Conversations',
    new_leads: 'New Leads',
    qualified_leads: 'Qualified',
    hot_leads: 'Hot Leads',
    demos_trials: 'Demos / Trials',
    signups: 'Signups',
    paid_customers: 'Paid',
    conversion_rate: 'Conversion %',
  };
  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {items.map((k) => (
        <div key={k.key} className="rounded-2xl bg-white border border-gray-100 p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{labels[k.key] ?? k.key}</p>
          <p className="mt-1 text-2xl font-black text-gray-900">{k.key === 'conversion_rate' ? `${k.value}%` : k.value}</p>
          <p className={`text-xs mt-1 ${k.trend === 'up' ? 'text-emerald-600' : k.trend === 'down' ? 'text-rose-600' : 'text-gray-400'}`}>
            {k.change_pct == null ? '—' : `${k.change_pct > 0 ? '+' : ''}${k.change_pct}% vs previous`}
          </p>
        </div>
      ))}
    </div>
  );
}

export function SalesFunnel({ stages }: { stages: FunnelStage[] }) {
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-5">
      <h3 className="text-sm font-bold text-gray-900 mb-4">Sales funnel</h3>
      <div className="space-y-3">
        {stages.map((s) => (
          <div key={s.key}>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-semibold text-gray-700">{s.label}</span>
              <span className="text-gray-500">
                {s.count}
                {s.conversion_from_previous != null ? ` · ${s.conversion_from_previous}%` : ''}
                {s.dropoff_from_previous != null && s.dropoff_from_previous > 0 ? ` · −${s.dropoff_from_previous}%` : ''}
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-teal-600 rounded-full" style={{ width: `${(s.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const LINKS = [
  { to: '/ai-sales', label: 'Overview', perm: 'ai_sales.view', end: true },
  { to: '/ai-sales/inbox', label: 'Inbox', perm: 'ai_sales.inbox' },
  { to: '/ai-sales/leads', label: 'Leads', perm: 'ai_sales.leads' },
  { to: '/ai-sales/follow-ups', label: 'Follow-ups', perm: 'ai_sales.followups' },
  { to: '/ai-sales/knowledge', label: 'Knowledge', perm: 'ai_sales.knowledge' },
  { to: '/ai-sales/agent', label: 'Agent', perm: 'ai_sales.agent' },
  { to: '/ai-sales/analytics', label: 'Analytics', perm: 'ai_sales.analytics' },
];

export function AiSalesSubnav() {
  const { can } = usePlatformPermissions();
  return (
    <div className="flex gap-1 overflow-x-auto pb-1 mb-5">
      {LINKS.filter((l) => can(l.perm) || can('ai_sales.view')).map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          className={({ isActive }) =>
            `px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${
              isActive ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
            }`
          }
        >
          {l.label}
        </NavLink>
      ))}
    </div>
  );
}
